import { ReactElement } from "react";
import { Link, RouteProps, useLocation } from "react-router-dom";
import "./index.css";
import { Drawer, Toolbar, List, ListItem, ListItemText } from "@mui/material";
import { InternalUserRoles, Role } from "../../constants/roles";
import { moduleRoutes } from "../../routing/routes/registry";
import AuthGate from "../../routing/components/AuthGate";
import FeatureGate from "../../routing/components/FeatureGate";

/**
 *
 * The main layout of the application. Uses a ThemeProvider to toggle
 * between a light and dark theme, and a React Unity Page component to set the layout of the page.
 *
 * To learn more about the ThemeProvider in React Unity, see: https://reactunity.azurewebsites.net/#/Theme?id=themeprovider
 *
 * To learn more about the Page and other possible layouts in React Unity, see: https://reactunity.azurewebsites.net/#/Layouts
 *
 */

const Navbar = (): ReactElement => {
  const { pathname } = useLocation();

  return (
    <Drawer className="navbar" variant="permanent">
      <Toolbar />
      <List className="lightText">
        {moduleRoutes.map((navBarListItem, index) => (
          <ProtectedNavMenuItem
            key={index}
            restrictedToRoles={navBarListItem.allowedRoles}
            featureFlag={navBarListItem.featureFlag}
            featureFlagBypassRoles={navBarListItem.featureFlagBypassRoles}
            label={navBarListItem.label}
            path={navBarListItem.path}
            icon={navBarListItem.icon}
            isSelected={pathname === navBarListItem.path}
          />
        ))}
      </List>
    </Drawer>
  );
};

export default Navbar;

interface ProtectedNavMenuItemProps extends RouteProps {
  restrictedToRoles?: Role[];
  featureFlag?: string;
  featureFlagBypassRoles?: Role[];
  label: string;
  path: string;
  icon: () => JSX.Element;
  isSelected: boolean;
}

const ProtectedNavMenuItem = ({
  restrictedToRoles = InternalUserRoles,
  featureFlag,
  featureFlagBypassRoles,
  label,
  path,
  icon,
  isSelected,
}: ProtectedNavMenuItemProps) => {
  const IconComponent = icon;
  const navItem = (
    <Link to={path} className="lightText">
      <ListItem className="lightText" button selected={isSelected}>
        <ListItemText
          disableTypography
          primary={<IconComponent />}
          secondary={<div>{label}</div>}
        />
      </ListItem>
    </Link>
  );

  return (
    <AuthGate restrictedToRoles={restrictedToRoles} fallback={<></>}>
      {featureFlag ? (
        <FeatureGate flag={featureFlag} bypassRoles={featureFlagBypassRoles} fallback={<></>}>
          {navItem}
        </FeatureGate>
      ) : (
        navItem
      )}
    </AuthGate>
  );
};
